import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function AccessibilityPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://overunderroofing.com';
    const existing = document.getElementById('accessibility-schema');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'accessibility-schema';
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Accessibility Statement – OverUnder Roofing',
      description: 'OverUnder Roofing is committed to making our website accessible to all users. Learn about our accessibility standards and how to contact us for assistance in Wake County, NC.',
      url: `${siteUrl}/accessibility`,
      isPartOf: { '@type': 'WebSite', name: 'OverUnder Roofing', url: siteUrl },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Accessibility Statement', item: `${siteUrl}/accessibility` },
        ],
      },
      publisher: { '@type': 'Organization', name: 'OverUnder Roofing', url: siteUrl },
    });
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('accessibility-schema');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* React 19 document metadata */}
      <title>Accessibility Statement | OverUnder Roofing – Cary, NC</title>
      <meta name="description" content="OverUnder Roofing is committed to making our website accessible to all users. Learn about our accessibility standards and how to contact us for assistance in Wake County, NC." />
      <link rel="canonical" href="https://overunderroofing.com/accessibility" />

      {/* Top Nav Strip */}
      <div className="bg-gray-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer">
            <i className="ri-arrow-left-line text-base text-emerald-400"></i>
            <span className="text-sm text-gray-300">Back to Home</span>
          </Link>
          <span className="text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>OverUnder Roofing</span>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">Legal</p>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            Accessibility Statement
          </h1>
          <p className="text-sm text-gray-500">Effective Date: March 25, 2026 &nbsp;·&nbsp; C&amp;K Baker Ventures LLC d/b/a OverUnder Roofing</p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Intro */}
        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-6 mb-10">
          <p className="text-gray-700 leading-relaxed">
            <strong>OverUnder Roofing</strong> is committed to making our website accessible and easy to use for everyone.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {/* Section 1 */}
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <div className="bg-gray-50 border-b border-gray-100 px-6 py-4 flex items-center gap-4">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-600 text-white text-sm font-bold flex-shrink-0">
                1
              </span>
              <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Our Commitment</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed mb-3">
                We strive to ensure our website is accessible to individuals of all abilities and aim to follow widely recognized accessibility standards.
              </p>
              <p className="text-gray-600 leading-relaxed">
                We are continuously working to improve the usability and accessibility of our site.
              </p>
            </div>
          </div>

          {/* Section 2 */}
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <div className="bg-gray-50 border-b border-gray-100 px-6 py-4 flex items-center gap-4">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-600 text-white text-sm font-bold flex-shrink-0">
                2
              </span>
              <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Need Assistance?</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed mb-3">
                If you experience any difficulty accessing content, navigating the site, or using any features, we&apos;re here to help.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Please contact us and we will do our best to provide the information you need in an accessible format.
              </p>
            </div>
          </div>

          {/* Section 3 */}
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <div className="bg-gray-50 border-b border-gray-100 px-6 py-4 flex items-center gap-4">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-600 text-white text-sm font-bold flex-shrink-0">
                3
              </span>
              <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Contact Us</h2>
            </div>
            <div className="px-6 py-5">
              <div className="bg-gray-50 rounded-lg p-5 border border-gray-100">
                <p className="font-semibold text-gray-800 mb-1">OverUnder Roofing</p>
                <p className="text-sm text-gray-500 mb-4">C&amp;K Baker Ventures LLC</p>
                <div className="flex flex-col gap-3 text-sm">
                  <a
                    href="tel:+19193497701"
                    className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 transition-colors cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-phone-line text-sm"></i>
                    </div>
                    (919) 349-7701
                  </a>
                  <a
                    href="mailto:overunderroofing@gmail.com"
                    className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 transition-colors cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-line text-sm"></i>
                    </div>
                    overunderroofing@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Closing Note */}
        <div className="mt-10 bg-emerald-50 border border-emerald-100 rounded-lg p-6">
          <p className="text-gray-700 leading-relaxed text-sm">
            We appreciate your feedback as we continue to improve accessibility for all users.
          </p>
        </div>

        {/* Contact Footer Block */}
        <div className="mt-12 border-t border-gray-200 pt-10">
          <div className="bg-gray-900 text-white rounded-lg p-8 text-center">
            <p className="text-lg font-bold mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>OverUnder Roofing</p>
            <p className="text-sm text-gray-400 mb-4">C&amp;K Baker Ventures LLC</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
              <a href="tel:+19193497701" className="text-emerald-400 hover:text-emerald-300 transition-colors cursor-pointer">
                (919) 349-7701
              </a>
              <span className="hidden sm:inline text-gray-600">·</span>
              <a href="mailto:overunderroofing@gmail.com" className="text-emerald-400 hover:text-emerald-300 transition-colors cursor-pointer">
                overunderroofing@gmail.com
              </a>
            </div>
          </div>
        </div>

        <p className="text-xs text-center text-gray-400 mt-6 mb-10">
          © 2026 OverUnder Roofing · C&amp;K Baker Ventures LLC · Wake County, NC
        </p>
      </div>
    </div>
  );
}
